import { useDispatch } from "react-redux";
import { useMutation, useQuery, useQueryCache } from "react-query";
import {
  checkIfDepartmentCanBeDeleted,
  createDepartment,
  deleteDepartment,
  fetchGetDepartments,
  getDepartment,
  getDepartmentPermissions,
  getDepartments,
  updateDepartment,
  updateDepartmentPermissions,
  Department as CoreDepartment,
} from "../queries/departments";
import { Department } from "../types/Department";
import useNetworkHeaders from "./useNetworkHeaders";
import { EMPLOYEES_LIST_QUERY_KEY } from "../actions/employees";

export default function useDepartments() {
  const dispatch = useDispatch();
  const { data, isLoading } = useQuery(["departments"], fetchGetDepartments(dispatch));
  if (data?.error) {
    return {
      data: [],
      isLoading: isLoading,
      dropdownOptions: [],
      error: data.error,
    };
  }

  const departments: Department[] = data?.payload.data || [];

  const dropdownOptions = departments.map((department) => {
    return {
      key: department.id,
      text: department.attributes.name,
      value: department.id,
    };
  });

  return { data: departments, isLoading, dropdownOptions };
}

export const DEPARTMENTS_LIST_QUERY_KEY = "coreDepartments" as const;
export const LEGACY_DEPARTMENTS_LIST_QUERY_KEY = "departments" as const;
export function useCoreDepartments({ onSuccess }: { onSuccess?: (data: CoreDepartment[]) => void } = {}) {
  const queryKey = DEPARTMENTS_LIST_QUERY_KEY;

  const networkHeaders = useNetworkHeaders();
  const departmentsQuery = useQuery(queryKey, getDepartments(networkHeaders), {
    onSuccess,
  });

  return { departmentsQuery, queryKey };
}

export const DEPARTMENT_QUERY_KEY = "coreDepartment" as const;
export const buildDepartmentQueryKey = (id: number) => [DEPARTMENT_QUERY_KEY, `${id}`] as const;
export function useCoreDepartment(id: number) {
  const networkHeaders = useNetworkHeaders();
  const departmentQuery = useQuery(buildDepartmentQueryKey(id), () => getDepartment(networkHeaders)(id), {
    enabled: !!id,
  });

  return { departmentQuery };
}

export function useUpdateCoreDepartment() {
  const networkHeaders = useNetworkHeaders();
  const queryCache = useQueryCache();
  const updateDepartmentMutation = useMutation(updateDepartment(networkHeaders), {
    onSuccess: ({ id }) => {
      queryCache.invalidateQueries(DEPARTMENTS_LIST_QUERY_KEY);
      queryCache.invalidateQueries(buildDepartmentQueryKey(id));
      queryCache.invalidateQueries(EMPLOYEES_LIST_QUERY_KEY);
      queryCache.invalidateQueries(LEGACY_DEPARTMENTS_LIST_QUERY_KEY);
    },
    throwOnError: true,
  });

  return updateDepartmentMutation;
}

export function useCreateCoreDepartment() {
  const networkHeaders = useNetworkHeaders();
  const queryCache = useQueryCache();
  const createDepartmentMutation = useMutation(createDepartment(networkHeaders), {
    onSuccess: () => {
      queryCache.invalidateQueries(DEPARTMENTS_LIST_QUERY_KEY);
      queryCache.invalidateQueries(EMPLOYEES_LIST_QUERY_KEY);
      queryCache.invalidateQueries(LEGACY_DEPARTMENTS_LIST_QUERY_KEY);
    },
    throwOnError: true,
  });

  return createDepartmentMutation;
}

export function useCheckDeleteDepartment() {
  const networkHeaders = useNetworkHeaders();
  const checkDeleteDepartment = async (id: number) => await checkIfDepartmentCanBeDeleted(networkHeaders)(id);
  return checkDeleteDepartment;
}

export function useDeleteDepartment() {
  const networkHeaders = useNetworkHeaders();
  const queryCache = useQueryCache();
  const deleteDepartmentMutation = useMutation(deleteDepartment(networkHeaders), {
    onSuccess: () => {
      queryCache.invalidateQueries(DEPARTMENTS_LIST_QUERY_KEY);
    },
    throwOnError: true,
  });

  return deleteDepartmentMutation;
}

export const DEPARTMENT_PERMISSIONS_QUERY_KEY = "coreDepartmentPermissions" as const;
export const buildDepartmentPermissionsQueryKey = (id: number) => [DEPARTMENT_PERMISSIONS_QUERY_KEY, `${id}`] as const;
export function useDepartmentPermissions(departmentId?: number | null) {
  const networkHeaders = useNetworkHeaders();
  const permissionsQuery = useQuery(
    buildDepartmentPermissionsQueryKey(departmentId!),
    () => getDepartmentPermissions(networkHeaders)(departmentId!),
    {
      enabled: !!departmentId,
    },
  );
  return { permissionsQuery };
}

export function useUpdateDepartmentPermissions() {
  const networkHeaders = useNetworkHeaders();
  const queryCache = useQueryCache();
  const updateDepartmentPermissionsMutation = useMutation(updateDepartmentPermissions(networkHeaders), {
    onSuccess: (_, { id }) => {
      queryCache.invalidateQueries(buildDepartmentPermissionsQueryKey(id));
    },
    throwOnError: true,
  });

  return updateDepartmentPermissionsMutation;
}
